import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import MealDetailContainer from '../meal/meal_detail_container';
import SmorgBoardShareModal from './smorg_board_share_modal';
import SmorgBoardImportModal from './smorg_board_import_modal';
import RecipesBoardView from './recipes_board_view';
import { useRecipesBoardId } from './recipes_hooks';
import RecipesEndUserAppView from './recipes_end_user_app_view';
import {
  appSyncGraphQLApi,
  useCreateSmorgBoardMutation,
  useGetSmorgBoardQuery,
  useGetSpaceQuery,
} from '../../app/smorgAppSyncGraphQLApi';
import { healthProGroupForSpaceID, userIsCreator } from '../../services/users';
import { DEFAULT_SPACE_ID } from '../../services/spaces';
import StaleScreenModal from '../common/stale_screen_modal';
import ResourceStatusControl from '../common/resource_status_control';
import {
  areResourcesStaleSelector,
  StaleResourceType,
} from '../../features/stale_resource_detection/stale_resource_detection_slice';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetOwnedRecipesBoardsQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { recipesBoardSelected } from '../../features/recipes/recipes_slice';
import {
  CURRENT_SMORG_BOARD_SCHEMA_VERSION,
  defaultMenuStructureForAdditionalBoard,
} from '../../services/smorg_board';

const SmorgBoardView = ({ showShareModal, showImportShareModal }) => {
  const recipesBoardId = useRecipesBoardId();

  const dispatch = useDispatch();

  const { data: userProfile } = useGetUserProfileQuery();

  const isSmorgStudio = userIsCreator(userProfile);

  const [searchParams] = useSearchParams();
  const openInRenameMode = searchParams.has('rename');

  useEffect(() => {
    dispatch(recipesBoardSelected(recipesBoardId));
  }, [recipesBoardId, dispatch]);

  const {
    data: ownedRecipesBoards,
    isLoading: isOwnedRecipesBoardsLoading,
    isError: isOwnedRecipesBoardsError,
  } = useGetOwnedRecipesBoardsQuery();

  const { data: space } = useGetSpaceQuery(undefined, { skip: !isSmorgStudio });

  const currentHealthProGroup = space && healthProGroupForSpaceID(space.id);

  const [createSmorgBoard] = useCreateSmorgBoardMutation();

  useEffect(() => {
    if (
      isSmorgStudio &&
      currentHealthProGroup &&
      !isOwnedRecipesBoardsLoading &&
      !isOwnedRecipesBoardsError &&
      (!ownedRecipesBoards || ownedRecipesBoards.length === 0)
    ) {
      console.log('Creating first recipes board for health pro');
      const blankBoard = {
        title: 'My Recipes',
        schemaVersion: CURRENT_SMORG_BOARD_SCHEMA_VERSION,
        groups: currentHealthProGroup && [currentHealthProGroup],
        menus: defaultMenuStructureForAdditionalBoard(),
      };
      createSmorgBoard(blankBoard);
    }
  }, [
    isOwnedRecipesBoardsLoading,
    isOwnedRecipesBoardsError,
    ownedRecipesBoards,
    isSmorgStudio,
    currentHealthProGroup,
    createSmorgBoard,
  ]);

  // const shouldShowOnboarding = userProfile?.showOnboarding;

  // const onboardingWizardDisabled = true;

  // useEffect(async () => {
  //   // if (!onboardingWizardDisabled) {
  //   //   return;
  //   // }
  //   if (shouldShowOnboarding) {
  //   const userProfileFieldsChoices = {};
  //   try {
  //     const mixpanelDistinctId = mixpanel.get_distinct_id();
  //     if (mixpanelDistinctId) {
  //       const mixpanelGeolocation = await getMixpanelGeolocationOperation(
  //         mixpanelDistinctId,
  //       );
  //       console.log({ mixpanelGeolocation });
  //       userProfileFieldsChoices.countryCode = mixpanelGeolocation.countryCode;
  //       if (
  //         !userProfileFieldsChoices.geoRegion &&
  //         userProfileFieldsChoices.countryCode
  //       ) {
  //         userProfileFieldsChoices.geoRegion = geoRegionFromCountryCode(
  //           userProfileFieldsChoices.countryCode,
  //         );
  //       }
  //     }
  //   } catch (e) {
  //     console.log('While fetching geolocation from Mixpanel');
  //     console.log(e);
  //   }
  //   const updatedUserProfile = {
  //     ...userProfile,
  //     ...userProfileFieldsChoices,
  //     showOnboarding: false,
  //   };
  //   await updateUserProfile(updatedUserProfile);
  //   showInitialGuide();
  //   }
  // }, [dispatch, /*onboardingWizardDisabled,*/ shouldShowOnboarding]);

  // const parsePivioRecipes = () => dispatch(addBulkRecipesAction(pivioLdJson, "91b00c16-3c80-4715-8150-3972bffaaf3b", "76616295-fb57-4c5a-b2c6-cacab7a47a24"));

  const { mealId } = useParams();

  const mealDetailVisibleFor = useMemo(() => {
    let visibleFor = {
      mealId: null,
      menuId: null,
    };

    if (mealId) {
      visibleFor = { mealId };
    }

    return visibleFor;
  }, [mealId]);

  const { sharedBoardID } = useParams();

  const mealDetailVisible = !!mealDetailVisibleFor.mealId;

  const navigate = useNavigate();

  const { data: recipesBoard } = useGetSmorgBoardQuery(recipesBoardId, {
    skip: !recipesBoardId,
  });

  const recipesBoardAndMealsLastUpdatedAt = recipesBoard?.updatedAt;

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery(
    undefined,
    {
      skip: !userProfile || isSmorgStudio,
    },
  );

  const isDefaultSpace = currentSpaceMembership?.spaceID === DEFAULT_SPACE_ID;

  // if (shouldShowOnboarding && !onboardingWizardDisabled) {
  //   return <OnboardingWizard />;
  // }

  const isSmorgBoardViewStale = useSelector((state) =>
    areResourcesStaleSelector(state, 'SmorgBoard', [recipesBoardId]),
  );

  const onReloadStaleData = () => {
    dispatch(
      appSyncGraphQLApi.util.invalidateTags([
        { type: 'SmorgBoard', id: recipesBoardId },
      ]),
    );
  };

  return (
    <>
      {(isSmorgStudio || (recipesBoardId && isDefaultSpace)) && (
        <RecipesBoardView
          recipesBoardId={recipesBoardId}
          openInRenameMode={openInRenameMode}
          mealDetailVisible={mealDetailVisible}
        />
      )}
      {!isSmorgStudio && !isDefaultSpace && (
        <RecipesEndUserAppView mealDetailVisible={mealDetailVisible} />
      )}
      {showShareModal && (
        <SmorgBoardShareModal
          recipesBoardId={recipesBoardId}
          recipesBoardAndMealsLastUpdatedAt={recipesBoardAndMealsLastUpdatedAt}
        />
      )}
      {showImportShareModal && (
        <SmorgBoardImportModal
          recipesBoardId={recipesBoardId}
          sharedBoardID={sharedBoardID}
        />
      )}
      {!isSmorgBoardViewStale && (
        <MealDetailContainer
          parentSmorgBoardId={recipesBoardId}
          menuId={mealDetailVisibleFor.menuId}
          mealId={mealDetailVisibleFor.mealId}
          visible={mealDetailVisible}
          dismiss={() => navigate(-1)}
        />
      )}
      {isSmorgBoardViewStale && <StaleScreenModal onSync={onReloadStaleData} />}
      <ResourceStatusControl
        resourceType={StaleResourceType.SmorgBoard}
        resourceIDs={[recipesBoardId]}
      />
    </>
  );
};

SmorgBoardView.propTypes = {
  showShareModal: PropTypes.bool,
  showImportShareModal: PropTypes.bool,
};

SmorgBoardView.defaultProps = {
  showShareModal: false,
  showImportShareModal: false,
};

export default SmorgBoardView;
